import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';
import type { OrderHistoryViewerQuery } from '@/__generated__/OrderHistoryViewerQuery.graphql';
import { OrderHistoryViewerQuery as OrderHistoryViewerQueryNode } from '@/features/order/graphql/OrderHistoryViewerQuery';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config/storageDatabase';
import { startTokenRefreshTimer } from '@/relay/tokenRefresh';
import { AppHeader } from './AppHeader';
import { LandingHeader } from './LandingHeader';
import { AppFooter } from "@lecartte/shared-ui/components/AppFooter";
import { SubscriptionModal } from '@/features/subscription/components/SubscriptionModal';
import { PlanModalProvider } from '@/features/subscription/contexts/PlanModalContext';

export const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [planModalInitial, setPlanModalInitial] = useState<string | null>(null);
  const data = useLazyLoadQuery<OrderHistoryViewerQuery>(OrderHistoryViewerQueryNode, {}, { fetchPolicy: 'store-and-network' });
  const role = data.viewer.user?.role;
  const isRestaurant = role === 'RESTAURANT' || role === 'ADMIN';
  const isRestaurantArea = location.pathname.startsWith('/restaurante/');
  const showRestaurantDashboard = isHydrated && isRestaurantArea && isRestaurant;

  useEffect(() => {
    setIsHydrated(true);

    // Iniciar verificação periódica de renovação de token
    if (isRestaurant) {
      startTokenRefreshTimer();
    }
  }, [isRestaurant]);

  // Deep-link de assinatura vindo do client (?assinar=<SLUG>).
  // Logado → abre o modal pré-selecionado no plano e limpa o param.
  // Deslogado → manda pro login preservando a intenção em returnTo.
  useEffect(() => {
    if (!isHydrated) return;
    const params = new URLSearchParams(location.search);
    const plan = params.get('assinar');
    if (!plan) return;

    if (isRestaurant) {
      setPlanModalInitial(plan);
      setIsPlanModalOpen(true);
      params.delete('assinar');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    } else {
      const returnTo = `${location.pathname}${location.search}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [isHydrated, isRestaurant, location.pathname, location.search, navigate]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      // Force page reload to clear Relay cache and ensure clean logout
      window.location.href = '/login';
    } else {
      navigate('/login');
    }
  };

  return (
    <PlanModalProvider onOpen={(plan) => { setPlanModalInitial(plan ?? null); setIsPlanModalOpen(true); }}>
      <div className="relative flex min-h-screen flex-col">
        {!isRestaurantArea && <LandingHeader />}
        {isRestaurantArea && (
          <AppHeader
            onLogout={handleLogout}
          />
        )}

        <main className="relative z-10 flex-1">
          <div className="mx-auto w-full max-w-7xl px-6 pb-10 pt-24">
            <Outlet />
          </div>
        </main>

        <AppFooter />

        <SubscriptionModal
          isOpen={isPlanModalOpen}
          initialPlan={planModalInitial}
          onClose={() => { setIsPlanModalOpen(false); setPlanModalInitial(null); }}
        />
      </div>
    </PlanModalProvider>
  );
};
